import React from "react";
import { Box, Checkbox } from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import PageInfoButton from "../PageInfoButton";
import { PAGE_HELP } from "./pageHelpRegistry";
import { helpIconSx } from "./pageInfoHelper";
import { useTheme } from "../../../contexts/ThemeContext";
import { getMm } from "../../../theme/modernMaterialEnterprise";

const SPECIAL_ICONS = {
  checkbox: (isDarkMode) => (
    <Checkbox sx={{ p: 0, mt: 0.5, "&.Mui-checked": { color: "#4CAF50" } }} size="small" checked />
  ),
  avatar: (isDarkMode) => (
    <Box
      component="span"
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        bgcolor: getMm(isDarkMode).outlineVariant,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.25rem",
      }}
    >
      👤
    </Box>
  ),
  green_smiley: () => (
    <Box
      component="span"
      sx={{
        width: 44,
        height: 44,
        borderRadius: "8px",
        bgcolor: "#4CAF50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.5rem",
      }}
    >
      😊
    </Box>
  ),
  red_smiley: () => (
    <Box
      component="span"
      sx={{
        width: 44,
        height: 44,
        borderRadius: "8px",
        bgcolor: "#EF4444",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.5rem",
      }}
    >
      😞
    </Box>
  ),
};

function resolveFeatureIcon(iconKey, isDarkMode) {
  if (!iconKey) return null;
  if (iconKey.startsWith("special:")) {
    const fn = SPECIAL_ICONS[iconKey.slice("special:".length)];
    return fn ? fn(isDarkMode) : null;
  }
  const Icon = MuiIcons[iconKey];
  if (!Icon) return null;
  return <Icon sx={helpIconSx(isDarkMode)} />;
}

/**
 * @param {{ pageKey: string, sx?: object, features?: Array, title?: string, overview?: string, importantNote?: string }} props
 * Pass `features` (and optional title/overview) to override registry — used by SettingsPage.
 */
export default function PageHelpButton({
  pageKey,
  sx = {},
  features: featuresOverride,
  title: titleOverride,
  overview: overviewOverride,
  importantNote: importantNoteOverride,
}) {
  const { isDarkMode } = useTheme();
  const config = PAGE_HELP[pageKey];

  if (!config && !featuresOverride) return null;

  const title = titleOverride ?? config?.title;
  const overview = overviewOverride ?? config?.overview;
  const importantNote = importantNoteOverride ?? config?.importantNote;
  const sourceFeatures = featuresOverride ?? config?.features ?? [];

  const features = sourceFeatures.map(({ icon, title: ft, description }) => ({
    icon: typeof icon === "string" ? resolveFeatureIcon(icon, isDarkMode) : icon,
    title: ft,
    description,
  }));

  return (
    <PageInfoButton
      title={title}
      overview={overview}
      features={features}
      importantNote={importantNote}
      sx={sx}
    />
  );
}
